import { Button, DirhamIcon } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { useUIColors } from '@/core/hooks';
import { trackReservationSummaryOpen } from '@/core/services/trackDynatraceCatalog';
import {
  getReservationToneColorToken,
  type ReservationStatus,
} from '@/core/reservation';
import { isSmallScreen } from '@/core/utils/responsive';
import React, { useEffect, useRef } from 'react';
import { Modal, Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import type { ReservationSignatureStatus } from './ReservationStatusCard';
import { ReservationStatusMessage } from './ReservationStatusMessage';

export interface ReservationSummarySheetProps {
  visible: boolean;
  onClose: () => void;
  totalPrice: number;
  iskanLoanAmount: number;
  requestedOnDisplay: string;
  status: ReservationSignatureStatus;
  mappedStatus: ReservationStatus | null;
  /** Raw SF status for ADHA Contentful `reservationStatus.<normalized>` override. */
  rawStatus?: string | null;
  onCancelReservation: () => void;
  canCancelReservation: boolean;
}

function formatAmount(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString() : '0';
}

function DashedDivider({ color }: Readonly<{ color: string }>) {
  return (
    <View
      style={[styles.dashedLine, { borderBottomColor: color }]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    />
  );
}

function AmountValue({
  amount,
  color,
  compact,
}: Readonly<{ amount: number; color: string; compact: boolean }>) {
  return (
    <View style={[styles.amountRow, compact ? styles.amountRowMobile : null]}>
      <View style={styles.dirhamWrap}>
        <DirhamIcon
          color={color}
          size={compact ? 14 : 16}
          {...(compact ? { width: 15, height: 14 } : {})}
        />
      </View>
      <Typography
        variant="body"
        color={color}
        style={[styles.rowValue, compact ? styles.amountValueMobile : null]}
      >
        {formatAmount(amount)}
      </Typography>
    </View>
  );
}

function getStatusColorToken(
  mappedStatus: ReservationStatus | null,
): string {
  if (!mappedStatus) {
    return 'reservationPendingText';
  }
  return getReservationToneColorToken(mappedStatus);
}

function StatusRow({
  isMobile,
  mappedStatus,
  rawStatus,
  statusColorToken,
  labels,
}: Readonly<{
  isMobile: boolean;
  mappedStatus: ReservationStatus | null;
  rawStatus?: string | null;
  statusColorToken: string;
  labels: UnitDetailsLabels;
}>) {
  return (
    <View style={[styles.row, isMobile ? styles.statusRowMobile : null]}>
      <SummaryRowLabel color="secondary" isMobile={isMobile}>
        {labels.RESERVATION_STATUS}
      </SummaryRowLabel>
      <View style={styles.statusValue}>
        <ReservationStatusMessage
          mappedStatus={mappedStatus}
          rawStatus={rawStatus ?? null}
          color={statusColorToken}
          variant="body"
          style={[
            styles.statusMessage,
            isMobile ? styles.statusMessageMobile : null,
          ]}
          skeletonWidth={160}
          skeletonHeight={16}
        />
      </View>
    </View>
  );
}

function SummaryRowLabel({
  color,
  isMobile,
  children,
}: Readonly<{
  color: string;
  isMobile: boolean;
  children: string;
}>) {
  return (
    <Typography
      variant="body"
      color={color}
      style={[styles.rowLabel, isMobile ? styles.rowLabelMobile : null]}
    >
      {children}
    </Typography>
  );
}

function SummaryRows({
  isMobile,
  totalPrice,
  iskanLoanAmount,
  requestedOnDisplay,
  mappedStatus,
  rawStatus,
  statusColorToken,
  fontPrimary,
  labels,
}: Readonly<{
  isMobile: boolean;
  totalPrice: number;
  iskanLoanAmount: number;
  requestedOnDisplay: string;
  mappedStatus: ReservationStatus | null;
  rawStatus?: string | null;
  statusColorToken: string;
  fontPrimary: string;
  labels: UnitDetailsLabels;
}>) {
  return (
    <View style={styles.rows}>
      <View style={styles.row}>
        <SummaryRowLabel color="secondary" isMobile={isMobile}>
          {labels.TOTAL_UNIT_PRICE}
        </SummaryRowLabel>
        <AmountValue
          amount={totalPrice}
          color={fontPrimary}
          compact={isMobile}
        />
      </View>
      <View style={styles.row}>
        <SummaryRowLabel color="secondary" isMobile={isMobile}>
          {labels.ISKAN_LOAN}
        </SummaryRowLabel>
        <AmountValue
          amount={iskanLoanAmount}
          color={fontPrimary}
          compact={isMobile}
        />
      </View>
      {requestedOnDisplay.trim() ? (
        <View style={styles.row}>
          <SummaryRowLabel color="secondary" isMobile={isMobile}>
            {labels.REQUESTED_ON}
          </SummaryRowLabel>
          <Typography
            variant="body"
            color="primary"
            style={[
              styles.rowValue,
              isMobile ? styles.dateTimeValueMobile : null,
            ]}
          >
            {requestedOnDisplay}
          </Typography>
        </View>
      ) : null}
      <StatusRow
        isMobile={isMobile}
        mappedStatus={mappedStatus}
        rawStatus={rawStatus ?? null}
        statusColorToken={statusColorToken}
        labels={labels}
      />
    </View>
  );
}

const SHEET_TABLET_MIN_WIDTH = 768;
const SHEET_TABLET_MAX_WIDTH = 1100;

function SummaryActions({
  onClose,
  onCancelReservation,
  canCancelReservation,
  labels,
}: Readonly<{
  onClose: () => void;
  onCancelReservation: () => void;
  canCancelReservation: boolean;
  labels: UnitDetailsLabels;
}>) {
  const { width } = useWindowDimensions();
  const isMobile = isSmallScreen(width);
  const isTablet =
    width >= SHEET_TABLET_MIN_WIDTH && width < SHEET_TABLET_MAX_WIDTH;
  const useHalfWidth = Platform.OS === 'web' || isTablet;

  if (!canCancelReservation) {
    return null;
  }

  if (isMobile) {
    return (
      <Button
        title={labels.CANCEL_RESERVATION_TITLE}
        variant="outline"
        fullWidth
        onPress={() => {
          onClose();
          onCancelReservation();
        }}
        style={styles.cancelButton}
        textStyle={styles.cancelButtonLabel}
      />
    );
  }

  return (
    <View style={styles.actionsRow}>
      <Pressable
        onPress={() => {
          onClose();
          onCancelReservation();
        }}
        accessibilityRole="button"
        accessibilityLabel={labels.CANCEL_RESERVATION_TITLE}
        style={[styles.cancelLinkWrap, useHalfWidth && styles.actionHalf]}
      >
        <Typography
          variant="headline"
          color="highContrast"
          style={styles.cancelLink}
        >
          {labels.CANCEL_RESERVATION_TITLE}
        </Typography>
      </Pressable>
    </View>
  );
}

function SummaryFooter({
  hasActions,
  onClose,
  onCancelReservation,
  canCancelReservation,
  dividerColor,
  labels,
}: Readonly<{
  hasActions: boolean;
  onClose: () => void;
  onCancelReservation: () => void;
  canCancelReservation: boolean;
  dividerColor: string;
  labels: UnitDetailsLabels;
}>) {
  if (!hasActions) {
    return null;
  }
  return (
    <>
      <DashedDivider color={dividerColor} />
      <SummaryActions
        onClose={onClose}
        onCancelReservation={onCancelReservation}
        canCancelReservation={canCancelReservation}
        labels={labels}
      />
    </>
  );
}

/**
 * Mobile bottom sheet opened from “View Reservation” while awaiting signature.
 * White sheet + centered grey summary card (matches appointment sheet pattern).
 */
export function ReservationSummarySheet({
  visible,
  onClose,
  totalPrice,
  iskanLoanAmount,
  requestedOnDisplay,
  mappedStatus,
  rawStatus,
  onCancelReservation,
  canCancelReservation,
}: Readonly<ReservationSummarySheetProps>) {
  const colors = useUIColors();
  const isMobile = isSmallScreen();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const hasActions = canCancelReservation;
  const statusColorToken = getStatusColorToken(mappedStatus);
  const openTrackedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      openTrackedRef.current = false;
      return;
    }
    if (openTrackedRef.current) {
      return;
    }
    openTrackedRef.current = true;
    trackReservationSummaryOpen();
  }, [visible]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View
        style={[styles.overlay, { backgroundColor: colors.modalBackdrop }]}
      >
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={labels.CLOSE}
        />
        <View
          style={[styles.sheet, { backgroundColor: colors.backgroundLevel1 }]}
        >
          <View style={styles.handleRow}>
            <View
              style={[
                styles.handle,
                { backgroundColor: colors.outlineDisabled },
              ]}
            />
          </View>

          <View style={styles.content}>
            <View
              style={[
                styles.card,
                { backgroundColor: colors.backgroundLevel2 },
              ]}
            >
              <Typography
                variant="headline"
                color="primary"
                style={styles.title}
              >
                {labels.RESERVATION_SUMMARY}
              </Typography>

              <DashedDivider color={colors.outlineDisabled} />

              <SummaryRows
                isMobile={isMobile}
                totalPrice={totalPrice}
                iskanLoanAmount={iskanLoanAmount}
                requestedOnDisplay={requestedOnDisplay}
                mappedStatus={mappedStatus}
                rawStatus={rawStatus ?? null}
                statusColorToken={statusColorToken}
                fontPrimary={colors.fontPrimary}
                labels={labels}
              />

              <SummaryFooter
                hasActions={hasActions}
                onClose={onClose}
                onCancelReservation={onCancelReservation}
                canCancelReservation={canCancelReservation}
                dividerColor={colors.outlineDisabled}
                labels={labels}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '90%',
  },
  handleRow: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  handle: {
    width: 60,
    height: 4,
    borderRadius: 4,
  },
  content: {
    paddingHorizontal: 20,
    paddingBottom: 24,
  },
  card: {
    borderRadius: 16,
    padding: 16,
    gap: 16,
  },
  title: {
    lineHeight: 16,
  },
  dashedLine: {
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    width: '100%',
  },
  rows: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  /** Small screens only — center Status label with wrapped value. */
  statusRowMobile: {
    alignItems: 'center',
  },
  rowLabel: {
    lineHeight: 22,
    flexShrink: 0,
  },
  rowLabelMobile: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 14,
    includeFontPadding: false,
    ...(Platform.OS === 'web' ? ({ verticalAlign: 'middle' } as object) : null),
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
  },
  amountRowMobile: {
    height: 14,
  },
  dirhamWrap: {
    width: 15,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  rowValue: {
    lineHeight: 22,
    flexShrink: 1,
    textAlign: 'right',
  },
  /** Small screen amount — Font/Size/S Regular, LH 100%, vertical-align middle */
  amountValueMobile: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 14,
    includeFontPadding: false,
    paddingTop: 0,
    paddingBottom: 0,
    ...(Platform.OS === 'web' ? ({ verticalAlign: 'middle' } as object) : null),
  },
  /** Small screen requested on value — Font/Size/S Regular, LH 100% */
  dateTimeValueMobile: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 14,
    includeFontPadding: false,
    paddingTop: 0,
    paddingBottom: 0,
    ...(Platform.OS === 'web' ? ({ verticalAlign: 'middle' } as object) : null),
  },
  statusValue: {
    flex: 1,
    minWidth: 0,
    maxWidth: 180,
    alignItems: 'flex-end',
  },
  statusMessage: {
    lineHeight: 22,
    textAlign: 'right',
  },
  /** Small screen: Font/Size/S Regular, LH 100% */
  statusMessageMobile: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 14,
  },
  /** Email hint — 3XS Regular, LH 100%, #666 (tertiary) */
  hintText: {
    lineHeight: 12,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cancelLinkWrap: {
    flexShrink: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  actionHalf: {
    flex: 1,
    flexShrink: 0,
  },
  cancelLink: {
    lineHeight: 16,
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web'
      ? ({ textUnderlineOffset: 6 } as object)
      : { paddingBottom: 2 }),
  },
  cancelButton: {
    width: '100%',
    backgroundColor: 'transparent',
    borderRadius: 4,
  },
  cancelButtonLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
  resendWrap: {
    flex: 1,
    minWidth: 0,
  },
  resendButton: {
    height: 48,
    borderRadius: 4,
    borderWidth: 2,
    padding: 12,
    backgroundColor: 'transparent',
  },
  resendLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
});

export default ReservationSummarySheet;
